import { useSignal } from '@preact/signals';
import type { ComponentType } from 'preact';
import { useLocation } from 'preact-iso';

import { Sheet } from '@amumin/ui';

import { messages } from '../state/session.js';
import { SheetPageContext } from './pageContext.js';

// Blad met een eigen adres (WP2, docs/ux/compact-plan.md: "Bladen en dialogen in plaats van
// pagina's"). Een detailpagina (Kamer, Recept, Lid, Bronnen, ...) opent als blad boven zijn
// ouderpagina: van onderen op de telefoon, als paneel rechts op de computer. Het adres blijft, dus
// e-mails, pushberichten, de QR van het bord en de knop Terug van de browser werken zoals eerst.
// Het scherm zelf verandert niet: zijn PageHeader geeft de titel aan de kop van het blad door en
// houdt zijn knoppen (lib/ui.tsx, SheetPageContext). Sluiten gaat naar het adres van de ouder.
//
// Gebruik (App.tsx), bv. voor Kamer:
//   <Route path="/bord/kamers/:id"
//     component={guard(sheetRoute(RoomsPage, RoomDetailPage, { name: 'room-sheet' }), {...})} />

export interface SheetRouteOptions {
  /** data-name van het blad (tests, meting). */
  name: string;
  /** Adres van de ouderpagina; standaard het pad zonder het laatste stuk. */
  parent?: ((path: string) => string) | undefined;
  /** Titel tot het scherm zijn eigen titel doorgeeft (laden). */
  title?: (() => string) | undefined;
}

/** Het adres van de ouder: het pad zonder het laatste stuk (/bord/kamers/12 → /bord/kamers). */
export function parentPath(path: string): string {
  const cut = path.replace(/\/+$/, '').replace(/\/[^/]*$/, '');
  return cut === '' ? '/' : cut;
}

export function sheetRoute(
  Parent: ComponentType,
  Child: ComponentType,
  options: SheetRouteOptions,
): ComponentType {
  return function SheetRoute() {
    const { path, route } = useLocation();
    const title = useSignal(options.title?.() ?? '');
    const close = () => route((options.parent ?? parentPath)(path));
    return (
      <>
        <Parent />
        <SheetPageContext.Provider value={{ setTitle: (t) => (title.value = t) }}>
          <Sheet
            open
            onClose={close}
            title={title.value}
            closeLabel={messages.value.design.dialog.close}
            name={options.name}
          >
            <Child />
          </Sheet>
        </SheetPageContext.Provider>
      </>
    );
  };
}
